import {observer} from 'mobx-react-lite';
import {State} from '../../../entity/model/state';
import {County} from '../../../entity/model/county';
import {TitleSummaryRequest} from '../../../entity/model/title-summary-request';
import {VestingDeed} from '../../../entity/resource/vesting-deed';
import {Estate} from '../../../entity/resource/estate';
import {TaxYear} from '../../../entity/resource/tax-year';
import {MobileHome, MobileHomeTaxYear} from '../../../entity/resource/mobile-home';
import {Mortgage} from '../../../entity/resource/mortgage';
import {Judgment} from '../../../entity/resource/judgment';
import {Lien} from '../../../entity/resource/lien';
import {UCC} from '../../../entity/resource/ucc';
import {RestrictionAgreement} from '../../../entity/resource/restriction-agreement';
import {EasementRightOfWay} from '../../../entity/resource/easement-right-of-way';
import {Assignment} from '../../../entity/resource/assignment';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {useService} from '../../../infrastructure/service/service';
import {useState, useEffect, useMemo} from 'react';
import {SearchType} from '../../../entity/enums/search-type';
import {DropdownOption} from '../../../entity/core/dropdown-option';
import {EstateStatus} from '../../../entity/enums/estate-status';
import {LienType} from '../../../entity/enums/lien-type';
import {ManageMode} from '../../../entity/enums/manage-mode';
import moment from 'moment/moment';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingComponent from '../components/widget/LoadingComponent';
import TitleSummaryTemplateDialog from '../components/report/TitleSummaryTemplateDialog';
import { toast } from 'react-toastify';
import {TitleSummaryTemplate} from '../../../entity/model/title-summary-template';
import {Section} from '../../../entity/resource/section';
import {Document} from '../../../entity/resource/document';

import '../../../assets/css/style.css';
import '../../../assets/css/pages/title-summary-report.css';

const TitleSummaryReport = (): JSX.Element => {
  const {titleSummaryRequestService, stateService, countyService, documentService} = useService();

  const [searchTypeOptions, setSearchTypeOptions] = useState<DropdownOption[]>([]);
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<ManageMode>(ManageMode.EXPORT);
  const [currentFormValues, setCurrentFormValues] = useState<TitleSummaryRequest>(titleSummaryRequestService.getTitleSummaryRequestDto());

  const initialFormValues = useMemo(() => currentFormValues, [currentFormValues]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      titleSummaryRequestService.loadSharedData();
      setCurrentFormValues(titleSummaryRequestService.getTitleSummaryRequestDto());

      const searchTypes = Object.keys(SearchType).map((key: string) => ({
        key: key,
        text: SearchType[key as keyof typeof SearchType],
        value: key
      }));
      setSearchTypeOptions(searchTypes);

      const statesData = await stateService.getStates();
      setStates(statesData);
      setStateOptions(statesData.map((state: State) => ({key: state.id, text: state.shortName, value: state.id})));

      const countiesData = await countyService.getCounties();
      setCounties(countiesData);

      const currentStateId = titleSummaryRequestService.getCurrentStateId();
      if (currentStateId) {
        const stateCounties = countiesData.filter((county: County) => county.stateID === currentStateId).sort((a: County, b: County) => a.name.localeCompare(b.name));
        setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));
      } else
        setCountyOptions(countiesData.filter((county: County) => county.stateID === statesData[0].id).sort((a: County, b: County) => a.name.localeCompare(b.name)).map((county: County) => ({key: county.id, text: county.name, value: county.id})));

      setIsDataLoaded(true);
    }

    init();
  }, []);

  const handleExportTemplate = (currentValues: TitleSummaryRequest): void => {
    setCurrentFormValues(currentValues);
    setDialogMode(ManageMode.EXPORT);
    setOpenTemplateDialog(true);
  }

  const handleImportTemplate = (): void => {
    setDialogMode(ManageMode.IMPORT);
    setOpenTemplateDialog(true);
  }

  const handleTemplateSelect = (template: TitleSummaryTemplate): void => {
    const updatedValues = { ...currentFormValues };

    if (template.orderInformation)
      updatedValues.orderInformation = template.orderInformation;

    if (template.propertyInformation)
      updatedValues.propertyInformation = template.propertyInformation;

    if (template.vestingDeeds)
      updatedValues.vestingDeeds = template.vestingDeeds;

    if (template.estates)
      updatedValues.estates = template.estates;

    if (template.taxYears)
      updatedValues.taxYears = template.taxYears;

    if (template.taxFlags)
      updatedValues.taxFlags = template.taxFlags;

    if (template.mobileHome)
      updatedValues.mobileHome = template.mobileHome;

    if (template.mortgages)
      updatedValues.mortgages = template.mortgages;

    if (template.judgments)
      updatedValues.judgments = template.judgments;

    if (template.liens)
      updatedValues.liens = template.liens;

    if (template.uccs)
      updatedValues.uccs = template.uccs;

    if (template.restrictionsAgreements)
      updatedValues.restrictionsAgreements = template.restrictionsAgreements;

    if (template.easementsRightOfWays)
      updatedValues.easementsRightOfWays = template.easementsRightOfWays;

    if (template.comment)
      updatedValues.comment = template.comment;

    setCurrentFormValues(updatedValues);
        
    const currentProps = titleSummaryRequestService.getCurrentProps();
    if (currentProps) {
      const reportDataWithPreservedIds = {
        ...updatedValues,
        _id: currentProps.reportData?._id ?? updatedValues._id,
        documentID: currentProps.reportData?.documentID ?? updatedValues.documentID,
        projectTitleID: currentProps.reportData?.projectTitleID ?? updatedValues.projectTitleID,
        createdByUserID: currentProps.reportData?.createdByUserID ?? updatedValues.createdByUserID,
        createdAt: currentProps.reportData?.createdAt ?? updatedValues.createdAt,
        updatedAt: currentProps.reportData?.updatedAt ?? updatedValues.updatedAt
      };
      
      titleSummaryRequestService.setCurrentProps({
        ...currentProps,
        reportData: reportDataWithPreservedIds
      });
    }
        
    toast.success('Template imported successfully!');
  }

  const handleSubmit = async (values: TitleSummaryRequest, {setSubmitting}: FormikHelpers<TitleSummaryRequest>): Promise<void> => {
    setSubmitting(true);

    const props = titleSummaryRequestService.getCurrentProps();
    const sanitizedData = titleSummaryRequestService.getSanitizeTitleSummaryRequest(values, props?.title.id);
    const state = states.find(state => state.id === values.propertyInformation?.stateID);
    const county = counties.find(county => county.id === values.propertyInformation?.countyID);

    const pdfBlob = await titleSummaryRequestService.generateTitleSummaryReportPdf(sanitizedData, state, county);

    if (!props?.title || !props?.project) {
      toast.error('Title or project information not available');
      setSubmitting(false);
      return;
    }

    const fileName = `Title_Summary_Report_${sanitizedData.orderInformation?.orderNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
    const pdfFile = new File([pdfBlob], fileName, {
      type: 'application/pdf',
      lastModified: Date.now()
    });

    let targetSection: Section | undefined;
    
    if (props.sectionId) 
      targetSection = props.title.sections?.find((section: Section) => section.id === props.sectionId);
    
    if (!targetSection) 
      targetSection = props.title.sections?.find((section: Section) => section.name === 'Unclassified');
    
    if (!targetSection) {
      toast.error('Section not found. Please create an "Unclassified" section first.');
      setSubmitting(false);
      return;
    }

    if (props.mode === ManageMode.UPDATE && props.reportData?.documentID) {
      const existingDocumentSection = props.title.sections?.find((section: Section) => section.documents?.some((doc: Document) => doc.id === props.reportData?.documentID));
            
      if (existingDocumentSection && props.reportData?.documentID) 
        await documentService.deleteDocument(props.title.createdByOrganizationID, existingDocumentSection.id, props.reportData.documentID, false, props.title.id);
    }

    const document = await documentService.uploadFile(props.project.createdByOrganizationID, pdfFile, props.title.id, targetSection.id);
    if (!document) {
      toast.error('Failed to upload document');
      setSubmitting(false);
      return;
    }

    sanitizedData.documentID = document.id;
        
    if (props.mode === ManageMode.UPDATE && props.reportData) 
      await titleSummaryRequestService.updateTitleSummaryRequest(props.project.createdByOrganizationID, props.reportData._id ?? '', sanitizedData);
    else 
      await titleSummaryRequestService.createTitleSummaryRequest(props.project.createdByOrganizationID, sanitizedData);

    const broadcastChannel = new BroadcastChannel('title-updates');
    broadcastChannel.postMessage({
      type: 'title-document-updated',
      titleId: props.title.id
    });
    broadcastChannel.close();
        
    setSubmitting(false);
  }

  const handleDeleteVestingDeed = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newDeeds = values.vestingDeeds?.filter((_: VestingDeed, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'vestingDeeds', value: newDeeds}});
  }

  const handleAddVestingDeed = (values: TitleSummaryRequest, handleChange: any): void => {
    const newDeed: VestingDeed = {
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      legalDescription: undefined
    };

    const newDeeds = [...(values.vestingDeeds ?? []), newDeed];
    handleChange({target: {name: 'vestingDeeds', value: newDeeds}});
  }

  const handleStateChange = (handleChange: any) => (event: any): void => {
    const stateCounties = counties.filter((county: County) => county.stateID === event.target.value).sort((a: County, b: County) => a.name.localeCompare(b.name));
    setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

    handleChange({target: {name: event.target.name, value: event.target.value}});
  }

  const handleCountyChange = (handleChange: any) => (event: any): void => {
    handleChange({target: {name: event.target.name, value: event.target.value}});
  }

  const handleAddEstate = (values: TitleSummaryRequest, handleChange: any): void => {
    const newEstate: Estate = {
      estateStatus: EstateStatus.OPEN,
      estateNumber: undefined,
      decedentName: undefined,
      heirsListed: false,
      heirsSearched: false
    };

    const newEstates = [...(values.estates ?? []), newEstate];
    handleChange({target: {name: 'estates', value: newEstates}});
  }

  const handleDeleteEstate = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newEstates = values.estates?.filter((_: Estate, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'estates', value: newEstates}});
  }

  const handleAddTaxYear = (values: TitleSummaryRequest, handleChange: any): void => {
    const newTaxYear: TaxYear = {
      year: '',
      amountOwed: '',
      amountPaid: ''
    };

    const newTaxYears = [...(values.taxYears ?? []), newTaxYear];
    handleChange({target: {name: 'taxYears', value: newTaxYears}});
  }

  const handleDeleteTaxYear = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newTaxYears = values.taxYears?.filter((_: TaxYear, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'taxYears', value: newTaxYears}});
  }

  const handleAddMobileHome = (values: TitleSummaryRequest, handleChange: any): void => {
    const newMobileHome: MobileHome = {
      year: undefined,
      make: undefined,
      model: undefined,
      serialNumber: undefined,
      parcelNumber: undefined,
      decal: undefined
    };

    const newMobileHomes = [...(values.mobileHome ?? []), newMobileHome];
    handleChange({target: {name: 'mobileHome', value: newMobileHomes}});
  }

  const handleDeleteMobileHome = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newMobileHomes = values.mobileHome?.filter((_: MobileHome, i: number) => i !== index) ?? [];
    handleChange({ target: { name: 'mobileHome', value: newMobileHomes }});
  }

  const handleAddMobileHomeTaxYear = (values: TitleSummaryRequest, mobileHomeIndex: number, handleChange: any): void => {
    const newTaxYear: MobileHomeTaxYear = {
      year: '',
      amountOwed: '',
      amountPaid: ''
    };

    const currentMobileHome = values.mobileHome?.[mobileHomeIndex];
    if (!currentMobileHome) return;

    const newTaxYears = [...(currentMobileHome.taxYears ?? []), newTaxYear];
    const updatedMobileHomes = [...(values.mobileHome ?? [])];
    updatedMobileHomes[mobileHomeIndex] = {
      ...currentMobileHome,
      taxYears: newTaxYears
    };

    handleChange({target: {name: 'mobileHome', value: updatedMobileHomes}});
  }

  const handleDeleteMobileHomeTaxYear = (values: TitleSummaryRequest, mobileHomeIndex: number, taxIndex: number, handleChange: any): void => {
    const currentMobileHome = values.mobileHome?.[mobileHomeIndex];
    if (!currentMobileHome) return;

    const newTaxYears = currentMobileHome.taxYears?.filter((_: MobileHomeTaxYear, i: number) => i !== taxIndex) ?? [];
    const updatedMobileHomes = [...(values.mobileHome ?? [])];
    updatedMobileHomes[mobileHomeIndex] = {
      ...currentMobileHome,
      taxYears: newTaxYears
    };

    handleChange({target: {name: 'mobileHome', value: updatedMobileHomes}});
  }

  const handleLienRetirementChange = (values: TitleSummaryRequest, mobileHomeIndex: number, handleChange: any) => (e: any): void => {
    const currentMobileHome = values.mobileHome?.[mobileHomeIndex];
    if (!currentMobileHome) return;

    const updatedMobileHomes = [...(values.mobileHome ?? [])];
    updatedMobileHomes[mobileHomeIndex] = {
      ...currentMobileHome,
      lienRetirement: e.target.checked ? (currentMobileHome.lienRetirement ?? {}) : undefined
    };

    handleChange({target: {name: 'mobileHome', value: updatedMobileHomes}});
  }

  const handleLienRetirementFieldChange = (values: TitleSummaryRequest, mobileHomeIndex: number, handleChange: any) => (e: any): void => {
    const currentMobileHome = values.mobileHome?.[mobileHomeIndex];
    if (!currentMobileHome) return;

    const fieldName = e.target.name.split('.').pop();
    const updatedMobileHomes = [...(values.mobileHome ?? [])];
    updatedMobileHomes[mobileHomeIndex] = {
      ...currentMobileHome,
      lienRetirement: {
        ...(currentMobileHome.lienRetirement ?? {}),
        [fieldName]: e.target.value
      }
    };

    handleChange({target: {name: 'mobileHome', value: updatedMobileHomes}});
  }

  const handleAddMortgage = (values: TitleSummaryRequest, handleChange: any): void => {
    const newMortgage: Mortgage = {
      lender: undefined,
      amount: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      assignments: []
    };

    const newMortgages = [...(values.mortgages ?? []), newMortgage];
    handleChange({target: {name: 'mortgages', value: newMortgages}});
  }

  const handleDeleteMortgage = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newMortgages = values.mortgages?.filter((_: Mortgage, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'mortgages', value: newMortgages}});
  }

  const handleAddMortgageAssignment = (values: TitleSummaryRequest, mortgageIndex: number, handleChange: any): void => {
    const newAssignment: Assignment = {
      lender: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined
    };

    const currentMortgage = values.mortgages?.[mortgageIndex];
    if (!currentMortgage) return;

    const newAssignments = [...(currentMortgage.assignments ?? []), newAssignment];
    const updatedMortgages = [...(values.mortgages ?? [])];
    updatedMortgages[mortgageIndex] = {
      ...currentMortgage,
      assignments: newAssignments
    };

    handleChange({target: {name: 'mortgages', value: updatedMortgages}});
  }

  const handleDeleteMortgageAssignment = (values: TitleSummaryRequest, mortgageIndex: number, assignmentIndex: number, handleChange: any): void => {
    const currentMortgage = values.mortgages?.[mortgageIndex];
    if (!currentMortgage) return;

    const newAssignments = currentMortgage.assignments?.filter((_: Assignment, i: number) => i !== assignmentIndex) ?? [];
    const updatedMortgages = [...(values.mortgages ?? [])];
    updatedMortgages[mortgageIndex] = {
      ...currentMortgage,
      assignments: newAssignments
    };

    handleChange({target: {name: 'mortgages', value: updatedMortgages}});
  }

  const handleAddJudgment = (values: TitleSummaryRequest, handleChange: any): void => {
    const newJudgment: Judgment = {
      plaintiff: undefined,
      defendant: undefined,
      amount: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      description: undefined
    };

    const newJudgments = [...(values.judgments ?? []), newJudgment];
    handleChange({target: {name: 'judgments', value: newJudgments}});
  }

  const handleDeleteJudgment = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newJudgments = values.judgments?.filter((_: Judgment, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'judgments', value: newJudgments}});
  }

  const handleAddLien = (values: TitleSummaryRequest, handleChange: any): void => {
    const newLien: Lien = {
      type: undefined,
      amount: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      description: undefined
    };

    const newLiens = [...(values.liens ?? []), newLien];
    handleChange({target: {name: 'liens', value: newLiens}});
  }

  const handleDeleteLien = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newLiens = values.liens?.filter((_: Lien, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'liens', value: newLiens}});
  }

  const handleAddUCC = (values: TitleSummaryRequest, handleChange: any): void => {
    const newUCC: UCC = {
      debtor: undefined,
      securedParty: undefined,
      filingDate: undefined,
      filingNumber: undefined,
      description: undefined
    };

    const newUCCs = [...(values.uccs ?? []), newUCC];
    handleChange({target: {name: 'uccs', value: newUCCs}});
  }

  const handleDeleteUCC = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newUCCs = values.uccs?.filter((_: UCC, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'uccs', value: newUCCs}});
  }

  const handleAddRestriction = (values: TitleSummaryRequest, handleChange: any): void => {
    const newRestriction: RestrictionAgreement = {
      title: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      description: undefined
    };

    const newRestrictions = [...(values.restrictionsAgreements ?? []), newRestriction];
    handleChange({target: {name: 'restrictionsAgreements', value: newRestrictions}});
  }

  const handleDeleteRestriction = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newRestrictions = values.restrictionsAgreements?.filter((_: RestrictionAgreement, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'restrictionsAgreements', value: newRestrictions}});
  }

  const handleAddEasement = (values: TitleSummaryRequest, handleChange: any): void => {
    const newEasement: EasementRightOfWay = {
      title: undefined,
      type: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      description: undefined
    };

    const newEasements = [...(values.easementsRightOfWays ?? []), newEasement];
    handleChange({target: {name: 'easementsRightOfWays', value: newEasements}});
  }

  const handleDeleteEasement = (values: TitleSummaryRequest, index: number, handleChange: any): void => {
    const newEasements = values.easementsRightOfWays?.filter((_: EasementRightOfWay, i: number) => i !== index) ?? [];
    handleChange({target: {name: 'easementsRightOfWays', value: newEasements}});
  }

  const handleEstatesYesChange = (values: any, handleChange: any) => (e: any): void => {
    if (e.target.checked && values.estates.length === 0)
      handleAddEstate(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'estates', value: []}});
  }

  const handleEstatesNoChange = (values: any, handleChange: any) => (e: any): void => {
    if (!e.target.checked && values.estates.length === 0)
      handleAddEstate(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'estates', value: []}});
  }

  const handleMobileHomeYesChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.mobileHome?.length ?? 0) === 0)
      handleAddMobileHome(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'mobileHome', value: []}});
  }

  const handleMobileHomeNoChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.mobileHome?.length ?? 0) === 0)
      handleAddMobileHome(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'mobileHome', value: []}});
  }

  const handleMortgagesYesChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.mortgages?.length ?? 0) === 0)
      handleAddMortgage(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'mortgages', value: []}});
  }

  const handleMortgagesNoChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.mortgages?.length ?? 0) === 0)
      handleAddMortgage(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'mortgages', value: []}});
  }

  const handleJudgmentsYesChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.judgments?.length ?? 0) === 0)
      handleAddJudgment(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'judgments', value: []}});
  }

  const handleJudgmentsNoChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.judgments?.length ?? 0) === 0)
      handleAddJudgment(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'judgments', value: []}});
  }

  const handleLiensYesChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.liens?.length ?? 0) === 0)
      handleAddLien(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'liens', value: []}});
  }

  const handleLiensNoChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.liens?.length ?? 0) === 0)
      handleAddLien(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'liens', value: []}});
  }

  const handleUCCsYesChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.uccs?.length ?? 0) === 0)
      handleAddUCC(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'uccs', value: []}});
  }

  const handleUCCsNoChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.uccs?.length ?? 0) === 0)
      handleAddUCC(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'uccs', value: []}});
  }

  const handleRestrictionsYesChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.restrictionsAgreements?.length ?? 0) === 0)
      handleAddRestriction(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'restrictionsAgreements', value: []}});
  }

  const handleRestrictionsNoChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.restrictionsAgreements?.length ?? 0) === 0)
      handleAddRestriction(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'restrictionsAgreements', value: []}});
  }

  const handleEasementsYesChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.easementsRightOfWays?.length ?? 0) === 0)
      handleAddEasement(values, handleChange);
    else if (!e.target.checked)
      handleChange({target: {name: 'easementsRightOfWays', value: []}});
  }

  const handleEasementsNoChange = (values: TitleSummaryRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.easementsRightOfWays?.length ?? 0) === 0)
      handleAddEasement(values, handleChange);
    else if (e.target.checked)
      handleChange({target: {name: 'easementsRightOfWays', value: []}});
  }

  const renderOrderInformation = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Order Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            id='input-order-number'
            fullWidth
            label='Order Number'
            name='orderInformation.orderNumber'
            value={values.orderInformation?.orderNumber ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.orderInformation?.orderNumber && Boolean(errors.orderInformation?.orderNumber)}
            helperText={touched.orderInformation?.orderNumber && errors.orderInformation?.orderNumber}/>
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth error={touched.orderInformation?.searchType && Boolean(errors.orderInformation?.searchType)}>
            <InputLabel>Search Type</InputLabel>
            <Select
              id='input-search-type'
              name='orderInformation.searchType'
              value={values.orderInformation?.searchType ?? ''}
              label='Search Type'
              onBlur={handleBlur}
              onChange={handleChange}>
              {
                searchTypeOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
            <FormHelperText>{touched.orderInformation?.searchType && errors.orderInformation?.searchType}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-search-start-date'
            fullWidth
            type='date'
            label='Search Start Date'
            name='orderInformation.searchStartDate'
            value={values.orderInformation?.searchStartDate ? moment(values.orderInformation.searchStartDate).local().format('YYYY-MM-DD') : ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.orderInformation?.searchStartDate && Boolean(errors.orderInformation?.searchStartDate)}
            helperText={touched.orderInformation?.searchStartDate && errors.orderInformation?.searchStartDate}
            InputLabelProps={{shrink: true}}/>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-search-end-date'
            fullWidth
            type='date'
            label='Search End Date'
            name='orderInformation.searchEndDate'
            value={values.orderInformation?.searchEndDate ? moment(values.orderInformation.searchEndDate).local().format('YYYY-MM-DD') : ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.orderInformation?.searchEndDate && Boolean(errors.orderInformation?.searchEndDate)}
            helperText={touched.orderInformation?.searchEndDate && errors.orderInformation?.searchEndDate}
            InputLabelProps={{shrink: true}}/>
        </Grid>
      </Grid>
    </Box>
  )

  const renderPropertyInformation = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Property Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <FormControl fullWidth error={touched.propertyInformation?.stateID && Boolean(errors.propertyInformation?.stateID)}>
            <InputLabel>State</InputLabel>
            <Select
              id='input-state'
              name='propertyInformation.stateID'
              value={values.propertyInformation?.stateID ?? ''}
              label='State'
              onBlur={handleBlur}
              onChange={handleStateChange(handleChange)}>
              {
                stateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
            <FormHelperText>{touched.propertyInformation?.stateID && errors.propertyInformation?.stateID}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth error={touched.propertyInformation?.countyID && Boolean(errors.propertyInformation?.countyID)}>
            <InputLabel>County</InputLabel>
            <Select
              id='input-county'
              name='propertyInformation.countyID'
              value={values.propertyInformation?.countyID ?? ''}
              label='County'
              onBlur={handleBlur}
              onChange={handleCountyChange(handleChange)}>
              {
                countyOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
            <FormHelperText>{touched.propertyInformation?.countyID && errors.propertyInformation?.countyID}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-address'
            fullWidth
            label='Address'
            name='propertyInformation.propertyAddress'
            value={values.propertyInformation?.propertyAddress ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.propertyInformation?.propertyAddress && Boolean(errors.propertyInformation?.propertyAddress)}
            helperText={touched.propertyInformation?.propertyAddress && errors.propertyInformation?.propertyAddress}/>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-parcel-number'
            fullWidth
            label='Parcel Number'
            name='propertyInformation.parcelNumber'
            value={values.propertyInformation?.parcelNumber ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.propertyInformation?.parcelNumber && Boolean(errors.propertyInformation?.parcelNumber)}
            helperText={touched.propertyInformation?.parcelNumber && errors.propertyInformation?.parcelNumber}/>
        </Grid>
      </Grid>
    </Box>
  );

  const renderOwnerInformation = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Owner Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            id='input-current-owners'
            fullWidth
            label='Current Owner(s)'
            name='propertyInformation.currentOwner'
            value={values.propertyInformation?.currentOwner ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.propertyInformation?.currentOwner && Boolean(errors.propertyInformation?.currentOwner)}
            helperText={touched.propertyInformation?.currentOwner && errors.propertyInformation?.currentOwner}/>
        </Grid>
      </Grid>
    </Box>
  );

  const renderVestingDeed = (values: TitleSummaryRequest, errors: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Vesting Deed
      </Typography>
      {
        values.vestingDeeds?.map((deed: VestingDeed, index: number) =>
          <Paper key={index} className='vesting-deed-item' elevation={1}>
            {
              (values.vestingDeeds?.length ?? 0) > 1 &&
                <IconButton
                  id={`btn-delete-vesting-deed-${index}`}
                  aria-label="delete"
                  onClick={() => handleDeleteVestingDeed(values, index, handleChange)}
                  className="vesting-deed-delete-button">
                  <DeleteIcon color="error"/>
                </IconButton>
            }
            <Grid container spacing={2}>
              <Grid size={3}>
                <TextField
                  id={`input-vesting-deed-recorded-${index}`}
                  fullWidth
                  type='date'
                  label='Recorded'
                  name={`vestingDeeds[${index}].recordedDate`}
                  value={deed.recordedDate ? moment(deed.recordedDate).local().format('YYYY-MM-DD') : ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeeds[${index}].recordedDate`])}
                  helperText={(errors as any)[`vestingDeeds[${index}].recordedDate`]}
                  InputLabelProps={{shrink: true}}/>
              </Grid>
              <Grid size={3}>
                <TextField
                  id={`input-vesting-deed-book-${index}`}
                  fullWidth
                  label='Book'
                  name={`vestingDeeds[${index}].book`}
                  value={deed.book ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeeds[${index}].book`])}
                  helperText={(errors as any)[`vestingDeeds[${index}].book`]}/>
              </Grid>
              <Grid size={3}>
                <TextField
                  id={`input-vesting-deed-page-${index}`}
                  fullWidth
                  label='Page'
                  name={`vestingDeeds[${index}].page`}
                  value={deed.page ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeeds[${index}].page`])}
                  helperText={(errors as any)[`vestingDeeds[${index}].page`]}/>
              </Grid>
              <Grid size={12}>
                <TextField
                  id={`input-vesting-deed-legal-description-${index}`}
                  fullWidth
                  label='Legal Description'
                  name={`vestingDeeds[${index}].legalDescription`}
                  value={deed.legalDescription ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeeds[${index}].legalDescription`])}
                  helperText={(errors as any)[`vestingDeeds[${index}].legalDescription`]}
                  multiline
                  rows={3}
                  className='zitles-textarea'/>
              </Grid>
            </Grid>
          </Paper>
        )}
      <Button
        id='btn-add-vesting-deed'
        variant='outlined'
        startIcon={<AddIcon/>}
        onClick={() => handleAddVestingDeed(values, handleChange)}
        className='zitles-button'>
        Add Additional Vesting Deed
      </Button>
    </Box>
  );

  const renderEstates = (values: TitleSummaryRequest, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Estates (Current Owner)
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-estates-yes'
              name='hasEstates'
              checked={(values.estates?.length ?? 0) > 0}
              onChange={handleEstatesYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-estates-no'
              name='hasEstates'
              checked={(values.estates?.length ?? 0) === 0}
              onChange={handleEstatesNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        (values.estates?.length ?? 0) > 0 &&
          <>
            {
              values.estates?.map((estate: Estate, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    (values.estates?.length ?? 0) > 1 &&
                      <IconButton
                      id={`btn-delete-estate-${index}`}
                      aria-label="delete"
                      onClick={() => handleDeleteEstate(values, index, handleChange)}
                      className="vesting-deed-delete-button">
                      <DeleteIcon color="error"/>
                    </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Estate
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <TextField
                        id={`input-estate-decedent-name-${index}`}
                        fullWidth
                        label="Decedent Name"
                        name={`estates[${index}].decedentName`}
                        value={estate.decedentName ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-estate-number-${index}`}
                        fullWidth
                        label="Estate Number"
                        name={`estates[${index}].estateNumber`}
                        value={estate.estateNumber ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          id={`input-estate-status-${index}`}
                          name={`estates[${index}].estateStatus`}
                          value={estate.estateStatus ?? ''}
                          label="Status"
                          onBlur={handleBlur}
                          onChange={handleChange}>
                          <MenuItem value={EstateStatus.OPEN}>Open</MenuItem>
                          <MenuItem value={EstateStatus.CLOSED}>Closed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <Box className="estates-checkbox-container">
                        <FormControlLabel
                          control={<Checkbox
                            id={`checkbox-estate-heirs-listed-${index}`}
                            name={`estates[${index}].heirsListed`}
                            checked={estate.heirsListed ?? false}
                            onChange={handleChange}/>}
                          label="Heirs Listed"/>
                        <FormControlLabel
                          control={<Checkbox
                            id={`checkbox-estate-heirs-searched-${index}`}
                            name={`estates[${index}].heirsSearched`}
                            checked={estate.heirsSearched ?? false}
                            onChange={handleChange}/>}
                          label="Heirs Searched"/>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )
            }
            <Button
              id="btn-add-estate"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddEstate(values, handleChange)}
              className="zitles-button">
              Add Estate
            </Button>
          </>
      }
    </Box>
  );

  const renderTaxes = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Taxes
      </Typography>
      {
        values.taxYears.map((taxYear: TaxYear, index: number) =>
          <Paper key={index} className='vesting-deed-item' elevation={1}>
            {
              values.taxYears.length > 1 &&
                <IconButton
                  id={`btn-delete-tax-year-${index}`}
                  aria-label="delete"
                  onClick={() => handleDeleteTaxYear(values, index, handleChange)}
                  className="vesting-deed-delete-button">
                  <DeleteIcon color="error"/>
                </IconButton>
            }
            <Typography variant='h6' gutterBottom className='order-dialog-section'>Tax</Typography>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField
                  id={`input-tax-year-${index}`}
                  fullWidth
                  label='Year'
                  name={`taxYears[${index}].year`}
                  value={taxYear.year ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}/>
              </Grid>
              <Grid size={4}>
                <TextField
                  id={`input-tax-amount-owed-${index}`}
                  fullWidth
                  label='Amount Owed'
                  name={`taxYears[${index}].amountOwed`}
                  value={taxYear.amountOwed ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    startAdornment: <InputAdornment position='start' className='dollar-sign-adornment'>$</InputAdornment>
                  }}/>
              </Grid>
              <Grid size={4}>
                <TextField
                  id={`input-tax-amount-paid-${index}`}
                  fullWidth
                  label='Amount Paid'
                  name={`taxYears[${index}].amountPaid`}
                  value={taxYear.amountPaid ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    startAdornment: <InputAdornment position='start' className='dollar-sign-adornment'>$</InputAdornment>
                  }}/>
              </Grid>
            </Grid>
          </Paper>
        )}
      <Button
        id='btn-add-tax-year'
        variant='outlined'
        startIcon={<AddIcon/>}
        onClick={() => handleAddTaxYear(values, handleChange)}
        className='zitles-button'>
        Add Additional Year
      </Button>
      <Box className='tax-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-pending-tax-sale'
              name='taxFlags.pendingTaxSale'
              checked={values.taxFlags?.pendingTaxSale ?? false}
              onChange={handleChange}/>
          }
          label='Pending Tax Sale?'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-delinquent-taxes'
              name='taxFlags.delinquentTaxes'
              checked={values.taxFlags?.delinquentTaxes ?? false}
              onChange={handleChange}/>
          }
          label='Delinquent Taxes?'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-roll-back-taxes'
              name='taxFlags.rollBackTaxes'
              checked={values.taxFlags?.rollBackTaxes ?? false}
              onChange={handleChange}/>
          }
          label='Roll Back Taxes?'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-tax-exempt'
              name='taxFlags.taxExempt'
              checked={values.taxFlags?.taxExempt ?? false}
              onChange={handleChange}/>
          }
          label='Tax Exempt?'/>
      </Box>
    </Box>
  );

  const renderMobileHome = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Mobile Home
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-mobile-home-yes'
              name='hasMobileHome'
              checked={values.mobileHome.length > 0}
              onChange={handleMobileHomeYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-mobile-home-no'
              name='hasMobileHome'
              checked={values.mobileHome.length === 0}
              onChange={handleMobileHomeNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.mobileHome.length > 0 &&
          <>
            {
              values.mobileHome.map((mobileHome: MobileHome, mobileIndex: number) =>
                <Paper key={mobileIndex} className="vesting-deed-item" elevation={1}>
                  {
                    values.mobileHome.length > 1 &&
                      <IconButton
                        id={`btn-delete-mobile-home-${mobileIndex}`}
                        aria-label="delete"
                        onClick={() => handleDeleteMobileHome(values, mobileIndex, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                    }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Mobile Home
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <TextField
                        id={`input-mobile-home-year-${mobileIndex}`}
                        fullWidth
                        label="Year"
                        name={`mobileHome[${mobileIndex}].year`}
                        value={mobileHome.year ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-mobile-home-make-${mobileIndex}`}
                        fullWidth
                        label="Make"
                        name={`mobileHome[${mobileIndex}].make`}
                        value={mobileHome.make ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-mobile-home-model-${mobileIndex}`}
                        fullWidth
                        label="Model"
                        name={`mobileHome[${mobileIndex}].model`}
                        value={mobileHome.model ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-mobile-home-serial-number-${mobileIndex}`}
                        fullWidth
                        label="Serial Number"
                        name={`mobileHome[${mobileIndex}].serialNumber`}
                        value={mobileHome.serialNumber ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-mobile-home-parcel-number-${mobileIndex}`}
                        fullWidth
                        label="Parcel Number"
                        name={`mobileHome[${mobileIndex}].parcelNumber`}
                        value={mobileHome.parcelNumber ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-mobile-home-decal-${mobileIndex}`}
                        fullWidth
                        label="Decal"
                        name={`mobileHome[${mobileIndex}].decal`}
                        value={mobileHome.decal ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <Box className="estates-checkbox-container">
                        <FormControlLabel
                          control={<Checkbox
                            id={`checkbox-mobile-home-lien-retirement-${mobileIndex}`}
                            name={`mobileHome[${mobileIndex}].lienRetirement`}
                            checked={mobileHome.lienRetirement !== undefined}
                            onChange={handleLienRetirementChange(values, mobileIndex, handleChange)}/>}
                          label="Lien Retirement?"/>
                        <FormControlLabel
                          control={<Checkbox
                            id={`checkbox-mobile-home-tax-years-${mobileIndex}`}
                            name={`mobileHome[${mobileIndex}].taxYears`}
                            checked={mobileHome.taxYears && mobileHome.taxYears.length > 0}
                            onChange={handleChange}/>}
                          label="Mobile Home Tax Years?"/>
                      </Box>
                    </Grid>
                    {
                      mobileHome.lienRetirement &&
                        <Grid size={12}>
                          <Typography variant="h6" gutterBottom className="order-dialog-section">
                            Lien Retirement
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid size={4}>
                              <TextField
                                id={`input-mobile-home-lien-retirement-recorded-${mobileIndex}`}
                                fullWidth
                                type="date"
                                label="Recorded Date"
                                name={`mobileHome[${mobileIndex}].lienRetirement.recordedDate`}
                                value={mobileHome.lienRetirement.recordedDate ? moment(mobileHome.lienRetirement.recordedDate).local().format('YYYY-MM-DD') : ''}
                                onChange={handleLienRetirementFieldChange(values, mobileIndex, handleChange)}
                                onBlur={handleBlur}
                                InputLabelProps={{shrink: true}}/>
                            </Grid>
                            <Grid size={4}>
                              <TextField
                                id={`input-mobile-home-lien-retirement-book-${mobileIndex}`}
                                fullWidth
                                label="Book"
                                name={`mobileHome[${mobileIndex}].lienRetirement.book`}
                                value={mobileHome.lienRetirement.book ?? ''}
                                onChange={handleLienRetirementFieldChange(values, mobileIndex, handleChange)}
                                onBlur={handleBlur}/>
                            </Grid>
                            <Grid size={4}>
                              <TextField
                                id={`input-mobile-home-lien-retirement-page-${mobileIndex}`}
                                fullWidth
                                label="Page"
                                name={`mobileHome[${mobileIndex}].lienRetirement.page`}
                                value={mobileHome.lienRetirement.page ?? ''}
                                onChange={handleLienRetirementFieldChange(values, mobileIndex, handleChange)}
                                onBlur={handleBlur}/>
                            </Grid>
                          </Grid>
                        </Grid>
                    }
                    {
                      mobileHome.taxYears && mobileHome.taxYears.length > 0 &&
                      <Grid size={12}>
                        <Typography variant="h6" gutterBottom className="order-dialog-section">
                          Mobile Home Tax Years
                        </Typography>
                        {
                          mobileHome.taxYears.map((taxYear: MobileHomeTaxYear, taxIndex: number) =>
                            <Paper key={taxIndex} className="vesting-deed-item" elevation={1}>
                              {
                                mobileHome.taxYears && mobileHome.taxYears.length > 1 &&
                                  <IconButton
                                  id={`btn-delete-mobile-home-tax-year-${mobileIndex}-${taxIndex}`}
                                  aria-label="delete"
                                  onClick={() => handleDeleteMobileHomeTaxYear(values, mobileIndex, taxIndex, handleChange)}
                                  className="vesting-deed-delete-button">
                                  <DeleteIcon color="error"/>
                                </IconButton>
                              }
                              <Grid container spacing={2}>
                                <Grid size={4}>
                                  <TextField
                                    id={`input-mobile-home-tax-year-${mobileIndex}-${taxIndex}`}
                                    fullWidth
                                    label="Year"
                                    name={`mobileHome[${mobileIndex}].taxYears[${taxIndex}].year`}
                                    value={taxYear.year ?? ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}/>
                                </Grid>
                                <Grid size={4}>
                                  <TextField
                                    id={`input-mobile-home-tax-amount-owed-${mobileIndex}-${taxIndex}`}
                                    fullWidth
                                    label="Amount Owed"
                                    name={`mobileHome[${mobileIndex}].taxYears[${taxIndex}].amountOwed`}
                                    value={taxYear.amountOwed ?? ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start" className="dollar-sign-adornment">$</InputAdornment>
                                    }}/>
                                </Grid>
                                <Grid size={4}>
                                  <TextField
                                    id={`input-mobile-home-tax-amount-paid-${mobileIndex}-${taxIndex}`}
                                    fullWidth
                                    label="Amount Paid"
                                    name={`mobileHome[${mobileIndex}].taxYears[${taxIndex}].amountPaid`}
                                    value={taxYear.amountPaid ?? ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start" className="dollar-sign-adornment">$</InputAdornment>
                                    }}/>
                                </Grid>
                              </Grid>
                            </Paper>
                          )
                        }
                        <Button
                          id={`btn-add-mobile-home-tax-year-${mobileIndex}`}
                          variant="outlined"
                          startIcon={<AddIcon/>}
                          onClick={() => handleAddMobileHomeTaxYear(values, mobileIndex, handleChange)}
                          className="zitles-button">
                          Add Mobile Home Tax Year
                        </Button>
                      </Grid>
                    }
                  </Grid>
                </Paper>
              )
            }
            <Button
              id="btn-add-mobile-home"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddMobileHome(values, handleChange)}
              className="zitles-button">
              Add Mobile Home
            </Button>
          </>
      }
    </Box>
  );

  const renderMortgages = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Mortgages
      </Typography>
      <Box className='mortgages-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-mortgages-yes'
              name='hasMortgages'
              checked={values.mortgages.length > 0}
              onChange={handleMortgagesYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-mortgages-no'
              name='hasMortgages'
              checked={values.mortgages.length === 0}
              onChange={handleMortgagesNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.mortgages.length > 0 &&
          <>
            {
              values.mortgages.map((mortgage: Mortgage, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    values.mortgages.length > 1 &&
                      <IconButton
                        id={`btn-delete-mortgage-${index}`}
                        aria-label="delete"
                        onClick={() => handleDeleteMortgage(values, index, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Mortgage
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <TextField
                        id={`input-mortgage-lender-${index}`}
                        fullWidth
                        label="Lender"
                        name={`mortgages[${index}].lender`}
                        value={mortgage.lender}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-mortgage-amount-${index}`}
                        fullWidth
                        label="Amount"
                        name={`mortgages[${index}].amount`}
                        value={mortgage.amount ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                          startAdornment: <InputAdornment position="start" className="dollar-sign-adornment">$</InputAdornment>
                        }}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-mortgage-recorded-${index}`}
                        fullWidth
                        type="date"
                        label="Recorded"
                        name={`mortgages[${index}].recordedDate`}
                        value={moment(mortgage.recordedDate).local().format('YYYY-MM-DD')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{shrink: true}}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-mortgage-book-${index}`}
                        fullWidth
                        label="Book"
                        name={`mortgages[${index}].book`}
                        value={mortgage.book}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-mortgage-page-${index}`}
                        fullWidth
                        label="Page"
                        name={`mortgages[${index}].page`}
                        value={mortgage.page}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                  </Grid>
                  <Box sx={{mt: 3, mb: 2}}>
                    <Button
                      id={`btn-add-mortgage-assignment-${index}`}
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon/>}
                      onClick={() => handleAddMortgageAssignment(values, index, handleChange)}
                      className="zitles-button">
                      Add Assignment
                    </Button>
                  </Box>
                  {
                    mortgage.assignments?.map((assignment: Assignment, assignmentIndex: number) =>
                      <Paper key={assignmentIndex} className="mortgage-assignment-paper" elevation={1}>
                        {
                          mortgage.assignments && mortgage.assignments.length > 1 &&
                            <IconButton
                              id={`btn-delete-mortgage-assignment-${index}-${assignmentIndex}`}
                              aria-label="delete"
                              onClick={() => handleDeleteMortgageAssignment(values, index, assignmentIndex, handleChange)}
                              className="mortgage-assignment-delete-button"
                              size="small">
                              <DeleteIcon color="error" fontSize="small"/>
                            </IconButton>
                        }
                        <Typography variant="h6" gutterBottom className="order-dialog-section">
                          Assignment
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={6}>
                            <TextField
                              id={`input-mortgage-assignment-lender-${index}-${assignmentIndex}`}
                              fullWidth
                              label="Lender"
                              name={`mortgages[${index}].assignments[${assignmentIndex}].lender`}
                              value={assignment.lender}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                          </Grid>
                          <Grid size={6}>
                            <TextField
                              id={`input-mortgage-assignment-recorded-${index}-${assignmentIndex}`}
                              fullWidth
                              type="date"
                              label="Recorded"
                              name={`mortgages[${index}].assignments[${assignmentIndex}].recordedDate`}
                              value={moment(assignment.recordedDate).local().format('YYYY-MM-DD')}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              InputLabelProps={{shrink: true}}/>
                          </Grid>
                          <Grid size={6}>
                            <TextField
                              id={`input-mortgage-assignment-book-${index}-${assignmentIndex}`}
                              fullWidth
                              label="Book"
                              name={`mortgages[${index}].assignments[${assignmentIndex}].book`}
                              value={assignment.book}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                          </Grid>
                          <Grid size={6}>
                            <TextField
                              id={`input-mortgage-assignment-page-${index}-${assignmentIndex}`}
                              fullWidth
                              label="Page"
                              name={`mortgages[${index}].assignments[${assignmentIndex}].page`}
                              value={assignment.page}
                              onChange={handleChange}
                              onBlur={handleBlur}/>
                          </Grid>
                        </Grid>
                      </Paper>
                    )
                  }
                </Paper>
              )
            }
            <Button
              id="btn-add-mortgage"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddMortgage(values, handleChange)}
              className="zitles-button">
              Add Mortgage
            </Button>
          </>
      }
    </Box>
  );

  const renderJudgments = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Judgments
      </Typography>
      <Box className='judgments-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-judgments-yes'
              name='hasJudgments'
              checked={values.judgments.length > 0}
              onChange={handleJudgmentsYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-judgments-no'
              name='hasJudgments'
              checked={values.judgments.length === 0}
              onChange={handleJudgmentsNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
                    (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.judgments.length > 0 &&
          <>
            {
              values.judgments.map((judgment: Judgment, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    values.judgments.length > 1 &&
                      <IconButton
                        id={`btn-delete-judgment-${index}`}
                        aria-label="delete"
                        onClick={() => handleDeleteJudgment(values, index, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Judgment
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <TextField
                        id={`input-judgment-plaintiff-${index}`}
                        fullWidth
                        label="Plaintiff"
                        name={`judgments[${index}].plaintiff`}
                        value={judgment.plaintiff}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-judgment-defendant-${index}`}
                        fullWidth
                        label="Defendant"
                        name={`judgments[${index}].defendant`}
                        value={judgment.defendant}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-judgment-amount-${index}`}
                        fullWidth
                        label="Amount"
                        name={`judgments[${index}].amount`}
                        value={judgment.amount ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                          startAdornment: <InputAdornment position="start" className="dollar-sign-adornment">$</InputAdornment>
                        }}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-judgment-recorded-${index}`}
                        fullWidth
                        type="date"
                        label="Recorded"
                        name={`judgments[${index}].recordedDate`}
                        value={moment(judgment.recordedDate).local().format('YYYY-MM-DD')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{shrink: true}}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-judgment-book-${index}`}
                        fullWidth
                        label="Book"
                        name={`judgments[${index}].book`}
                        value={judgment.book}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-judgment-page-${index}`}
                        fullWidth
                        label="Page"
                        name={`judgments[${index}].page`}
                        value={judgment.page}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={`input-judgment-description-${index}`}
                        fullWidth
                        label="Description"
                        name={`judgments[${index}].description`}
                        value={judgment.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={3}
                        className="zitles-textarea"/>
                    </Grid>

                  </Grid>
                </Paper>
              )
            }
            <Button
              id="btn-add-judgment"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddJudgment(values, handleChange)}
              className="zitles-button">
              Add Judgment
            </Button>
          </>
      }
    </Box>
  );

  const renderLiens = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Liens
      </Typography>
      <Box className='liens-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-liens-yes'
              name='hasLiens'
              checked={values.liens.length > 0}
              onChange={handleLiensYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-liens-no'
              name='hasLiens'
              checked={values.liens.length === 0}
              onChange={handleLiensNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.liens.length > 0 &&
          <>
            {
              values.liens.map((lien: Lien, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    values.liens.length > 1 &&
                      <IconButton
                        id={`btn-delete-lien-${index}`}
                        aria-label="delete"
                        onClick={() => handleDeleteLien(values, index, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Lien
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                          id={`input-lien-type-${index}`}
                          name={`liens[${index}].type`}
                          value={lien.type ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}>
                          {Object.keys(LienType).map((key: string) => (<MenuItem key={key} value={LienType[key as keyof typeof LienType]}>
                              {LienType[key as keyof typeof LienType]}
                            </MenuItem>))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-lien-amount-${index}`}
                        fullWidth
                        label="Amount"
                        name={`liens[${index}].amount`}
                        value={lien.amount ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                          startAdornment: <InputAdornment position="start" className="dollar-sign-adornment">$</InputAdornment>
                        }}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-lien-recorded-${index}`}
                        fullWidth
                        type="date"
                        label="Recorded"
                        name={`liens[${index}].recordedDate`}
                        value={moment(lien.recordedDate).local().format('YYYY-MM-DD')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{shrink: true}}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-lien-book-${index}`}
                        fullWidth
                        label="Book"
                        name={`liens[${index}].book`}
                        value={lien.book}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-lien-page-${index}`}
                        fullWidth
                        label="Page"
                        name={`liens[${index}].page`}
                        value={lien.page}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={`input-lien-description-${index}`}
                        fullWidth
                        label="Description"
                        name={`liens[${index}].description`}
                        value={lien.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={3}
                        className="zitles-textarea"/>
                    </Grid>
                  </Grid>
                </Paper>
              )
            }
            <Button
              id="btn-add-lien"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddLien(values, handleChange)}
              className="zitles-button">
              Add Lien
            </Button>
          </>
      }
    </Box>
  );

  const renderUCCs = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        UCCs
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-uccs-yes'
              name='hasUCCs'
              checked={values.uccs.length > 0}
              onChange={handleUCCsYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-uccs-no'
              name='hasUCCs'
              checked={values.uccs.length === 0}
              onChange={handleUCCsNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.uccs.length > 0 &&
          <>
            {
              values.uccs.map((ucc: UCC, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    values.uccs.length > 1 &&
                      <IconButton
                        id={`btn-delete-ucc-${index}`}
                        aria-label="delete"
                        onClick={() => handleDeleteUCC(values, index, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    UCC Filing
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <TextField
                        id={`input-ucc-debtor-${index}`}
                        fullWidth
                        label="Debtor"
                        name={`uccs[${index}].debtor`}
                        value={ucc.debtor}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-ucc-secured-party-${index}`}
                        fullWidth
                        label="Secured Party"
                        name={`uccs[${index}].securedParty`}
                        value={ucc.securedParty}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-ucc-filing-date-${index}`}
                        fullWidth
                        type="date"
                        label="Filing Date"
                        name={`uccs[${index}].filingDate`}
                        value={moment(ucc.filingDate).local().format('YYYY-MM-DD')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{shrink: true}}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        id={`input-ucc-filing-number-${index}`}
                        fullWidth
                        label="Filing Number"
                        name={`uccs[${index}].filingNumber`}
                        value={ucc.filingNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={`input-ucc-description-${index}`}
                        fullWidth
                        label="Description"
                        name={`uccs[${index}].description`}
                        value={ucc.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={3}
                        className="zitles-textarea"/>
                    </Grid>
                  </Grid>
                </Paper>
              )
            }
            <Button
              id="btn-add-ucc"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddUCC(values, handleChange)}
              className="zitles-button">
              Add UCC
            </Button>
          </>
      }
    </Box>
  );

  const renderRestrictions = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Restrictions / Agreements
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-restrictions-yes'
              name='hasRestrictions'
              checked={values.restrictionsAgreements.length > 0}
              onChange={handleRestrictionsYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-restrictions-no'
              name='hasRestrictions'
              checked={values.restrictionsAgreements.length === 0}
              onChange={handleRestrictionsNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.restrictionsAgreements.length > 0 &&
          <>
            {
              values.restrictionsAgreements.map((restriction: RestrictionAgreement, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    values.restrictionsAgreements.length > 1 &&
                      <IconButton
                        id={`btn-delete-restriction-${index}`}
                        aria-label="delete"
                        onClick={() => handleDeleteRestriction(values, index, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Restriction/Agreement
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <TextField
                        id={`input-restriction-book-${index}`}
                        fullWidth
                        label="Book"
                        name={`restrictionsAgreements[${index}].book`}
                        value={restriction.book ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-restriction-page-${index}`}
                        fullWidth
                        label="Page"
                        name={`restrictionsAgreements[${index}].page`}
                        value={restriction.page ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        id={`input-restriction-recorded-date-${index}`}
                        fullWidth
                        type="date"
                        label="Recorded Date"
                        name={`restrictionsAgreements[${index}].recordedDate`}
                        value={restriction.recordedDate ? moment(restriction.recordedDate).local().format('YYYY-MM-DD') : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{shrink: true}}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={`input-restriction-title-${index}`}
                        fullWidth
                        label="Title"
                        name={`restrictionsAgreements[${index}].title`}
                        value={restriction.title ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={`input-restriction-description-${index}`}
                        fullWidth
                        label="Description"
                        name={`restrictionsAgreements[${index}].description`}
                        value={restriction.description ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={3}
                        className="zitles-textarea"/>
                    </Grid>
                  </Grid>
                </Paper>
              )
            }
            <Button
              id="btn-add-restriction"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddRestriction(values, handleChange)}
              className="zitles-button">
              Add Restriction/Agreement
            </Button>
          </>
      }
    </Box>
  );

  const renderEasements = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Easements & Right of Ways
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-easements-yes'
              name='hasEasements'
              checked={values.easementsRightOfWays.length > 0}
              onChange={handleEasementsYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              id='checkbox-easements-no'
              name='hasEasements'
              checked={values.easementsRightOfWays.length === 0}
              onChange={handleEasementsNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.easementsRightOfWays.length > 0 &&
          <>
            {
              values.easementsRightOfWays.map((easement: EasementRightOfWay, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    values.easementsRightOfWays.length > 1 &&
                      <IconButton
                        id={`btn-delete-easement-${index}`}
                        aria-label="delete"
                        onClick={() => handleDeleteEasement(values, index, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Easement
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={3}>
                      <TextField
                        id={`input-easement-book-${index}`}
                        fullWidth
                        label="Book"
                        name={`easementsRightOfWays[${index}].book`}
                        value={easement.book ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={3}>
                      <TextField
                        id={`input-easement-page-${index}`}
                        fullWidth
                        label="Page"
                        name={`easementsRightOfWays[${index}].page`}
                        value={easement.page ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={3}>
                      <TextField
                        id={`input-easement-recorded-date-${index}`}
                        fullWidth
                        type="date"
                        label="Recorded Date"
                        name={`easementsRightOfWays[${index}].recordedDate`}
                        value={easement.recordedDate ? moment(easement.recordedDate).local().format('YYYY-MM-DD') : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{shrink: true}}/>
                    </Grid>
                    <Grid size={3}>
                      <TextField
                        id={`input-easement-type-${index}`}
                        fullWidth
                        label="Type"
                        name={`easementsRightOfWays[${index}].type`}
                        value={easement.type ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={`input-easement-title-${index}`}
                        fullWidth
                        label="Title"
                        name={`easementsRightOfWays[${index}].title`}
                        value={easement.title ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        id={`input-easement-description-${index}`}
                        fullWidth
                        label="Description"
                        name={`easementsRightOfWays[${index}].description`}
                        value={easement.description ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={3}
                        className="zitles-textarea"/>
                    </Grid>
                  </Grid>
                </Paper>
              )
            }
            <Button
              id="btn-add-easement"
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddEasement(values, handleChange)}
              className="zitles-button">
              Add Easement
            </Button>
          </>
      }
    </Box>
  );

  const renderGeneralComments = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        General Comments
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            id='input-general-comments'
            fullWidth
            label='General Comments'
            name='comment'
            value={values.comment ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            rows={4}
            className='zitles-textarea'/>
        </Grid>
      </Grid>
    </Box>
  );

  const renderActionButtons = (isSubmitting: boolean): JSX.Element => {
    const props = titleSummaryRequestService.getCurrentProps();
    const isUpdate = props?.mode === ManageMode.UPDATE && Boolean(props?.reportData);
        
    return (
      <Box className='action-btn-container'>
        <Button
          id='btn-submit-report'
          type='submit'
          disabled={isSubmitting}
          className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
          startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
          {isSubmitting ? (isUpdate ? 'Updating Report' : 'Uploading Report') : (isUpdate ? 'Update Report' : 'Save Report')}
        </Button>
      </Box>
    );
  };

  return (
    <Box className='title-summary-report-container'>
      {
        !isDataLoaded
          ? <LoadingComponent />
          : <Paper elevation={2} className='title-summary-report-paper'>
              <Formik
                initialValues={initialFormValues}
                enableReinitialize={true}
                onSubmit={handleSubmit}>
                {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting}: FormikProps<TitleSummaryRequest>): JSX.Element => {
                  return (
                    <>
                      <Box className='title-summary-report-header'>
                        <Typography variant='h5' className='zitles-dialog-title report-title'>
                          Title Summary Report
                        </Typography>
                        <Box className='template-buttons-container'>
                          <Button
                            id='btn-import-template'
                            variant='outlined'
                            startIcon={<DownloadIcon/>}
                            onClick={handleImportTemplate}
                            className='zitles-button'>
                            Import Template
                          </Button>
                          <Button
                            id='btn-export-template'
                            variant='outlined'
                            startIcon={<UploadIcon/>}
                            onClick={() => handleExportTemplate(values)}
                            className='zitles-button'>
                            Export Template
                          </Button>
                        </Box>
                      </Box>
                      <Form onSubmit={handleSubmit}>
                        {renderOrderInformation(values, errors, touched, handleChange, handleBlur)}

                        {renderPropertyInformation(values, errors, touched, handleChange, handleBlur)}

                        {renderOwnerInformation(values, errors, touched, handleChange, handleBlur)}

                        {renderVestingDeed(values, errors, handleChange, handleBlur)}

                        {renderEstates(values, handleChange, handleBlur)}

                        {renderTaxes(values, handleChange, handleBlur)}

                        {renderMobileHome(values, handleChange, handleBlur)}

                        {renderMortgages(values, handleChange, handleBlur)}

                        {renderJudgments(values, handleChange, handleBlur)}

                        {renderLiens(values, handleChange, handleBlur)}

                        {renderUCCs(values, handleChange, handleBlur)}

                        {renderRestrictions(values, handleChange, handleBlur)}

                        {renderEasements(values, handleChange, handleBlur)}

                        {renderGeneralComments(values, handleChange, handleBlur)}

                        {renderActionButtons(isSubmitting)}
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </Paper>
      }
      {
        openTemplateDialog &&
          <TitleSummaryTemplateDialog
            open={openTemplateDialog}
            onClose={() => setOpenTemplateDialog(false)}
            titleSummaryRequest={titleSummaryRequestService.getSanitizeTitleSummaryRequest(currentFormValues)}
            mode={dialogMode}
            onTemplateSelect={dialogMode === ManageMode.IMPORT ? handleTemplateSelect : undefined}/>}
    </Box>
  );
}

export default observer(TitleSummaryReport);
